import React from 'react';
import {
  Form,
  RecipientAddressForm,
  IRecipientAddressFormValues,
  FormField,
  ValidationError,
  FormProps,
  FormButtons,
  Button,
  FormSchemaEntry,
  recipientAddressFormSchema,
  IRecipientAddressFormErrors,
} from '@gik/ui';
import { useTranslation } from 'react-i18next';
import { translationKeys } from './i18n/en';
import bemBlock from '@gik/core/utils/bemBlock';
import { FormRenderProps } from 'react-final-form';
import Checkbox from '@gik/ui/Checkbox';
import { recipientFullNameField } from '@gik/core/form/formSchemas';

export interface IRecipientAddressPromptFormProps extends FormProps {
  buttonsPortal?: () => HTMLElement;
  isRecipientFullNameRequired?: boolean;
  onSkip?: () => Promise<void>;
  isDontAskAgain?: boolean;
  setIsDontAskAgain?: (checked: boolean) => void;
}

export interface IRecipientAddressPromptFormValues extends IRecipientAddressFormValues {
  recipientFullName: string;
}

export interface IRecipientAddressPromptFormErrors extends IRecipientAddressFormErrors {
  recipientFullName: ValidationError;
}

export enum recipientAddressPromptInputNames {
  RecipientFullName = 'recipientFullName',
}

const recipientFullNameCustomProps = (isRecipientFullNameRequired: boolean) =>
  ({
    name: recipientAddressPromptInputNames.RecipientFullName,
    required: isRecipientFullNameRequired,
  } as FormSchemaEntry);

export const recipientAddressPromptFormSchema: (isRecipientFullNameRequired?: boolean) => FormSchemaEntry[] = (
  isRecipientFullNameRequired = false
) => [recipientFullNameField(recipientFullNameCustomProps(isRecipientFullNameRequired))];

export const recipientAddressPromptFormBlockName = 'recipient-address-prompt-form';
const formId = 'recipient-address-prompt-form';

export function RecipientAddressPromptForm({
  buttonsPortal,
  className,
  isRecipientFullNameRequired = false,
  onSkip,
  isDontAskAgain,
  setIsDontAskAgain,
  ...otherProps
}: IRecipientAddressPromptFormProps): React.ReactElement {
  const { t } = useTranslation();
  const bem = bemBlock(recipientAddressPromptFormBlockName);

  const schema: FormSchemaEntry[] = recipientAddressPromptFormSchema(isRecipientFullNameRequired).concat(
    recipientAddressFormSchema(true)
  );

  const buttons = (form: FormRenderProps) => (
    <>
      <Checkbox
        checked={isDontAskAgain}
        onValueChange={setIsDontAskAgain}
        className={bem('dont-ask-checkbox')}
        label={t(translationKeys.dontAskAgain)}
      />
      <Button className={bem('skip-button')} onClick={onSkip} variant="default">
        {t(translationKeys.skipButton)}
      </Button>
      <Button
        className={bem('submit-button')}
        variant="primary"
        type="submit"
        loading={form.submitting}
        disabled={!form.valid}
        form={formId}
      >
        {t(translationKeys.submitButton)}
      </Button>
    </>
  );

  return (
    <Form
      id={formId}
      className={bem(null, null, className)}
      variant="primary"
      schema={schema}
      render={(form: FormRenderProps) => (
        <>
          <FormField name={recipientAddressPromptInputNames.RecipientFullName} />
          <RecipientAddressForm form={form} required={true} />

          <FormButtons buttons={() => buttons(form)} buttonsPortal={buttonsPortal} form={form} formId={formId} />
        </>
      )}
      {...otherProps}
    />
  );
}
